import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Check } from "lucide-react";
import type { Rsp } from "@repo/types";
import { AuthAPI } from "@repo/api/auth";
import { UserAPI } from "@repo/api/user";
import { Button, FieldError, Input, Label, Spinner, TextField } from "@heroui/react";
import { PhoneInput } from "@/components/from/phone-input";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { asSearchString } from "@repo/utils/search-params";
import { F2A_Biz } from "@/pages/f2a/-const";

export type AccountRecoverySearch = {
  recovery_ticket?: string;
  session_code?: string;
};

export const Route = createFileRoute("/user/account/recovery")({
  validateSearch: (search: Record<string, unknown>): AccountRecoverySearch => ({
    recovery_ticket: asSearchString(search.recovery_ticket),
    session_code: asSearchString(search.session_code),
  }),
  component: AccountRecovery,
});

const routeApi = getRouteApi("/user/account/recovery");

export default function AccountRecovery() {
  const navigate = useNavigate();
  const { recovery_ticket: recoveryTicket, session_code: sessionCode } = routeApi.useSearch();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [countryCode, setCountryCode] = useState("86");
  const [password, setPassword] = useState({ pass: "", confirmPass: "" });
  const [loading, setLoading] = useState(false);
  const passwordMismatch = password.confirmPass.length > 0 && password.pass !== password.confirmPass;

  useDocumentTitle("账号找回");

  const isPasswordValid = useMemo(() => {
    if (password.pass.length < 8) return false;
    if (!password.pass.match(/[a-z]/g)?.length) return false;
    if (!password.pass.match(/[A-Z]/g)?.length) return false;
    if (!password.pass.match(/\d/g)?.length) return false;
    if (!password.pass.match(/[^a-zA-Z0-9]/g)?.length) return false;
    if (password.pass !== password.confirmPass) return false;
    return true;
  }, [password.pass, password.confirmPass]);

  const canSubmit = phoneNumber && smsCode && isPasswordValid;

  const handleGetSmsCode = () => {
    if (!phoneNumber) {
      toast.error("请输入手机号");
      return Promise.reject();
    }
    if (!countryCode) {
      toast.error("请选择国家区号");
      return Promise.reject();
    }
    return AuthAPI.getSMSCode(phoneNumber, countryCode, F2A_Biz.ACCOUNT_RECOVERY).then(
      (res) => {
        if (res.status !== 0) {
          toast.error(res.msg || "验证码发送失败");
          return Promise.reject();
        }
        toast.success("验证码发送成功");
      },
      (err: Rsp<null>) => {
        toast.error(err.msg || "验证码发送失败");
        return Promise.reject();
      },
    );
  };

  const handleSubmit = () => {
    if (!recoveryTicket || !sessionCode) {
      toast.error("授权失效，请重新开始");
      return;
    }
    if (!phoneNumber) {
      toast.error("请输入手机号");
      return;
    }
    if (!smsCode) {
      toast.error("请输入验证码");
      return;
    }
    if (!isPasswordValid) {
      toast.error("密码不符合要求");
      return;
    }

    setLoading(true);
    UserAPI.completeRecovery({
      recovery_ticket: recoveryTicket,
      session_code: sessionCode,
      country_code: countryCode,
      phone_number: phoneNumber,
      code: smsCode,
      password: password.pass,
    })
      .then(
        () => {
          navigate({
            to: "/result/success",
            search: {
              type: "success",
              title: "账户已更新",
              end: "true",
              op: "返回登录",
              action: "close_webview",
            },
            replace: true,
          });
        },
        (err: Rsp<null>) => {
          toast.error(err.msg);
        },
      )
      .finally(() => setLoading(false));
  };

  return (
    <div className="p-4 max-w-md mx-auto">
      <div className="text-3xl font-bold text-gray-800 mb-1 mt-4">更新账号信息</div>
      <div className="text-md text-gray-500 mb-6">绑定新手机号并设置新密码</div>
      <form className="flex flex-col gap-6" onSubmit={(e) => e.preventDefault()}>
        <section className="flex flex-col gap-3">
          <div className="text-lg font-medium text-gray-800">绑定新手机号</div>
          <PhoneInput
            value={phoneNumber}
            onChange={setPhoneNumber}
            onGetCode={handleGetSmsCode}
            codeValue={smsCode}
            onCodeChange={setSmsCode}
            countryCode={countryCode}
            onCountryCodeChange={setCountryCode}
            disabled={loading}
          />
        </section>

        <section className="flex flex-col gap-4">
          <div className="text-lg font-medium text-gray-800">设置新密码</div>
          <TextField
            name="password"
            type="password"
            fullWidth
            isDisabled={loading}
            value={password.pass}
            onChange={(newPass) => {
              setPassword({ ...password, pass: newPass });
            }}
          >
            <Label>新密码</Label>
            <Input placeholder="请输入新密码" />
          </TextField>
          <PasswordStrengthIndicator
            length={password.pass.length}
            lower={password.pass.match(/[a-z]/g)?.length || 0}
            upper={password.pass.match(/[A-Z]/g)?.length || 0}
            number={password.pass.match(/\d/g)?.length || 0}
            special={password.pass.match(/[^a-zA-Z0-9]/g)?.length || 0}
          />
          <TextField
            name="confirmPass"
            type="password"
            fullWidth
            isDisabled={loading}
            isInvalid={passwordMismatch}
            value={password.confirmPass}
            onChange={(newConfirm) => {
              setPassword({ ...password, confirmPass: newConfirm });
            }}
          >
            <Label>确认密码</Label>
            <Input placeholder="请再次输入新密码" />
            {passwordMismatch && <FieldError>两次输入的密码不一致</FieldError>}
          </TextField>
        </section>

        <Button
          className="w-full rounded-full mt-2"
          type="button"
          isPending={loading}
          isDisabled={!canSubmit || loading}
          onPress={handleSubmit}
        >
          {({ isPending }) => (
            <>
              {isPending && <Spinner size="sm" color="current" />}
              完成
            </>
          )}
        </Button>
      </form>
    </div>
  );
}

function PasswordStrengthIndicator({
  length,
  lower,
  upper,
  number,
  special,
}: {
  length: number;
  lower: number;
  upper: number;
  number: number;
  special: number;
}) {
  return (
    <div className="flex flex-col gap-2 text-sm">
      <div className="flex flex-row gap-2">
        <Check color={length >= 8 ? "#96d35f" : "#f00"} size={16} />
        密码长度至少8位
      </div>
      <div className="flex flex-row gap-2">
        <Check color={lower > 0 ? "#96d35f" : "#f00"} size={16} />
        密码至少包含一个字母
      </div>
      <div className="flex flex-row gap-2">
        <Check color={upper > 0 ? "#96d35f" : "#f00"} size={16} />
        密码至少包含一个大写字母
      </div>
      <div className="flex flex-row gap-2">
        <Check color={number > 0 ? "#96d35f" : "#f00"} size={16} />
        密码至少包含一个数字
      </div>
      <div className="flex flex-row gap-2">
        <Check color={special > 0 ? "#96d35f" : "#f00"} size={16} />
        密码至少包含一个特殊字符
      </div>
    </div>
  );
}
